import { FC, useMemo, useState } from "react";
import { Card, Tag, Empty, Button, Drawer } from "antd";
import { Calendar, ChevronLeft, ChevronRight, MapPin, Users, Package, Clock, X, CalendarDays, Truck, Box } from "lucide-react";
import { capitalize, formatFullAddress, getClientName } from "~/utils";
import dayjs from "dayjs";
import isoWeek from "dayjs/plugin/isoWeek";
import { TServiceTicket } from "~/types";
import { JobStatus, ServiceType } from "~/utils/constants";
import { getStatusBadge } from "../booking-utils";

dayjs.extend(isoWeek);

type Props = {
  serviceTickets: TServiceTicket[];
};

export const ServicesCalendarView: FC<Props> = ({ serviceTickets }) => {
  const [currentWeekStart, setCurrentWeekStart] = useState(dayjs().startOf("isoWeek"));
  const [selectedService, setSelectedService] = useState<TServiceTicket | null>(null);

  // Group tickets by date
  const ticketsByDate = useMemo(() => {
    const grouped: Record<string, TServiceTicket[]> = {};

    serviceTickets.forEach((ticket) => {
      const date = dayjs(ticket.serviceDatetime || ticket.startDatetime).format("YYYY-MM-DD");
      if (!grouped[date]) {
        grouped[date] = [];
      }
      grouped[date].push(ticket);
    });

    // Sort tickets within each date by time
    Object.keys(grouped).forEach((date) => {
      grouped[date].sort((a, b) => {
        const timeA = dayjs(a.serviceDatetime || a.startDatetime);
        const timeB = dayjs(b.serviceDatetime || b.startDatetime);
        return timeA.diff(timeB);
      });
    });

    return grouped;
  }, [serviceTickets]);

  const getStatusColor = (status: string) => {
    const statusMap: Record<string, string> = {
      [JobStatus.COMPLETED]: "success",
      [JobStatus.IN_PROGRESS]: "processing",
      [JobStatus.SCHEDULED]: "blue",
      [JobStatus.DRAFT]: "default",
      [JobStatus.CANCELLED]: "error",
      [JobStatus.ESTIMATED]: "warning",
    };
    return statusMap[status] || "default";
  };

  const navigateWeeks = (direction: "prev" | "next") => {
    if (direction === "prev") {
      setCurrentWeekStart(currentWeekStart.subtract(2, "week"));
    } else {
      setCurrentWeekStart(currentWeekStart.add(2, "week"));
    }
  };

  const goToToday = () => {
    setCurrentWeekStart(dayjs().startOf("isoWeek"));
  };

  // Generate 14 days starting from currentWeekStart
  const twoWeekDays = useMemo(() => {
    const days = [];
    for (let i = 0; i < 14; i++) {
      days.push(currentWeekStart.add(i, "day"));
    }
    return days;
  }, [currentWeekStart]);

  const endDate = currentWeekStart.add(13, "day");

  return (
    <div className="flex gap-4">
      {/* Calendar Grid */}
      <div className={`${selectedService ? 'w-3/5' : 'w-full'} transition-all duration-300`}>
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Calendar className="text-blue-600" size={24} />
              <div>
                <h3 className="text-lg font-semibold">Calendar View</h3>
                <p className="text-sm text-gray-500">
                  {currentWeekStart.format("MMM D")} - {endDate.format("MMM D, YYYY")}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <Button onClick={() => navigateWeeks("prev")} icon={<ChevronLeft size={16} />}>
                Previous
              </Button>
              <Button onClick={goToToday}>Today</Button>
              <Button onClick={() => navigateWeeks("next")} icon={<ChevronRight size={16} />}>
                Next
              </Button>
            </div>
          </div>

          <div className="grid grid-cols-7 gap-3">
            {twoWeekDays.map((day) => {
              const dateKey = day.format("YYYY-MM-DD");
              const tickets = ticketsByDate[dateKey] || [];
              const isToday = day.isSame(dayjs(), "day");
              const isPast = day.isBefore(dayjs(), "day");

              return (
                <div
                  key={dateKey}
                  className={`bg-white border rounded-lg p-2 min-h-[180px] ${
                    isToday ? "border-blue-500 border-2" : ""
                  } ${isPast ? "bg-gray-50" : ""}`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <div className={`text-xs font-medium ${isToday ? "text-blue-600" : "text-gray-600"}`}>
                        {day.format("ddd")}
                      </div>
                      <div
                        className={`text-xl font-bold ${isToday ? "text-blue-600" : isPast ? "text-gray-400" : "text-gray-900"}`}
                      >
                        {day.format("D")}
                      </div>
                    </div>
                    {tickets.length > 0 && (
                      <div className="bg-blue-100 text-blue-600 text-xs font-medium px-1.5 py-0.5 rounded-full">
                        {tickets.length}
                      </div>
                    )}
                  </div>

                  <div className="space-y-2">
                    {tickets.length === 0 ? (
                      <div className="text-xs text-gray-400 text-center py-4">No services</div>
                    ) : (
                      tickets.map((ticket) => {
                        // Combine date from serviceDatetime with time from startDatetime
                        let displayTime;
                        if (ticket.serviceDatetime && ticket.startDatetime) {
                          const serviceDate = dayjs(ticket.serviceDatetime);
                          const startTimeData = dayjs(ticket.startDatetime);
                          displayTime = serviceDate
                            .hour(startTimeData.hour())
                            .minute(startTimeData.minute())
                            .second(startTimeData.second());
                        } else {
                          displayTime = dayjs(ticket.serviceDatetime || ticket.startDatetime);
                        }

                        const isSelected = selectedService?.id === ticket.id;

                        return (
                          <Card
                            key={ticket.id}
                            size="small"
                            className={`cursor-pointer hover:shadow-md transition-shadow ${
                              isSelected ? 'ring-2 ring-blue-500 shadow-md' : ''
                            } ${ticket.isExpoDay ? 'border-purple-300 bg-purple-50/50' : ''}`}
                            bodyStyle={{ padding: "8px" }}
                            onClick={() => setSelectedService(ticket)}
                          >
                            <div className="space-y-1">
                              <div className="flex items-center justify-between">
                                <span className="text-xs font-medium text-blue-600">{ticket.serial}</span>
                                {ticket.isExpoDay && (
                                  <Tag color="purple" className="text-xs px-1 py-0 leading-tight">EXPO</Tag>
                                )}
                              </div>

                              {ticket.isExpoDay && ticket.dayLabel && (
                                <div className="text-xs font-semibold text-purple-700">
                                  {ticket.dayLabel}
                                </div>
                              )}

                              <div className="text-xs font-medium text-gray-700">
                                {displayTime.format("h:mm A")}
                              </div>

                              {!ticket.isExpoDay && (
                                <div className="text-xs text-gray-600 truncate">
                                  {capitalize(ticket.serviceType)}
                                </div>
                              )}

                              <div className="flex items-center gap-3 text-xs text-gray-500">
                                {ticket.isExpoDay && ticket.positions && ticket.positions.length > 0 ? (
                                  <div className="flex items-center gap-1 text-purple-600">
                                    <Users size={10} />
                                    <span>{ticket.positions.reduce((sum, p) => sum + p.quantity, 0)} staff</span>
                                  </div>
                                ) : (
                                  <>
                                    {ticket.staffs && ticket.staffs.length > 0 && (
                                      <div className="flex items-center gap-1">
                                        <Users size={10} />
                                        <span>{ticket.staffs.length}</span>
                                      </div>
                                    )}
                                    {ticket.inventories && ticket.inventories.length > 0 && (
                                      <div className="flex items-center gap-1">
                                        <Package size={10} />
                                        <span>{ticket.inventories.length}</span>
                                      </div>
                                    )}
                                  </>
                                )}
                              </div>
                            </div>
                          </Card>
                        );
                      })
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Right Sidebar - Service Details */}
      {selectedService && (
        <div className="w-2/5 border rounded-lg bg-white sticky top-4 h-fit max-h-[calc(100vh-200px)] overflow-y-auto">
          {/* Header */}
          <div className="p-4 border-b bg-gray-50 sticky top-0 z-10">
            <div className="flex justify-between items-start">
              <div className="flex items-center space-x-3">
                {selectedService.isExpoDay ? (
                  <CalendarDays size={24} className="text-purple-600" />
                ) : selectedService.serviceType === ServiceType.COLLECTION || selectedService.serviceType === ServiceType.DELIVERY ? (
                  <Truck size={24} className="text-blue-600" />
                ) : (
                  <Box size={24} className="text-blue-600" />
                )}
                <div>
                  <div className="font-semibold text-lg">
                    {selectedService.isExpoDay ? (
                      <span className="text-purple-700">{selectedService.dayLabel || 'Expo Day'}</span>
                    ) : (
                      capitalize(selectedService.serviceType)
                    )}
                  </div>
                  <div className="text-sm text-gray-500">{selectedService.serial}</div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {selectedService.isExpoDay && (
                  <Tag color="purple" className="flex items-center gap-1">
                    <CalendarDays size={12} />
                    EXPO
                  </Tag>
                )}
                {getStatusBadge(selectedService.status)}
                <button
                  onClick={() => setSelectedService(null)}
                  className="p-1 hover:bg-gray-200 rounded"
                >
                  <X size={16} />
                </button>
              </div>
            </div>
            <div className="mt-2 flex items-center text-sm text-gray-600">
              <Calendar size={14} className="mr-1" />
              {selectedService.serviceDatetime ? (
                <>
                  {new Date(selectedService.serviceDatetime).toLocaleDateString('en-US', {
                    weekday: 'long',
                    month: 'numeric',
                    day: 'numeric',
                    year: 'numeric'
                  })}
                  {selectedService.startDatetime && (
                    <span className="ml-3">
                      <Clock size={14} className="inline mr-1" />
                      {dayjs(selectedService.startDatetime).format("h:mm A")} • {selectedService.duration || 'TBD'} hrs
                    </span>
                  )}
                </>
              ) : (
                <span className="text-gray-400 italic">Date TBD</span>
              )}
            </div>
          </div>

          {/* Content */}
          <div className="p-4 space-y-4">
            {/* Location */}
            <div>
              <div className="text-sm text-gray-500 mb-1">Location</div>
              <div className="font-medium">
                {selectedService.location
                  ? formatFullAddress(selectedService.location)
                  : selectedService.tempLocationData
                    ? `${selectedService.tempLocationData.city || ''}${selectedService.tempLocationData.state ? ', ' + selectedService.tempLocationData.state : ''}${selectedService.tempLocationData.zip ? ' ' + selectedService.tempLocationData.zip : ''}`.trim() || 'Not specified'
                    : 'Not specified'}
              </div>
            </div>

            {/* Scope */}
            {selectedService.scope && (
              <div>
                <div className="text-sm text-gray-500 mb-1">Scope</div>
                <div className="font-medium">{selectedService.scope}</div>
              </div>
            )}

            {/* Staff (for non-expo) */}
            {!selectedService.isExpoDay && selectedService.staffs && selectedService.staffs.length > 0 && (
              <div>
                <div className="text-sm text-gray-500 mb-1">Assigned Staff</div>
                <div className="flex flex-wrap gap-2">
                  {selectedService.staffs.map((staff, idx) => (
                    <div key={idx} className="px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-xs">
                      {getClientName(staff.user)}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Expo Positions Section */}
            {selectedService.isExpoDay && selectedService.positions && selectedService.positions.length > 0 && (
              <div>
                <div className="text-sm font-medium mb-2 flex items-center gap-2">
                  <CalendarDays size={16} className="text-purple-600" />
                  <span>Expo Positions</span>
                </div>
                <div className="border border-purple-200 rounded-lg overflow-hidden">
                  <div className="grid grid-cols-4 gap-2 text-xs font-medium text-gray-500 bg-purple-50 px-3 py-2">
                    <div>Position</div>
                    <div>Qty</div>
                    <div>Start</div>
                    <div>Hrs</div>
                  </div>
                  {selectedService.positions.map((pos, idx) => (
                    <div key={pos.id || idx} className="grid grid-cols-4 gap-2 text-sm px-3 py-2 border-t border-purple-100">
                      <div className="font-medium truncate">
                        {pos.position?.name || 'Unknown'}
                      </div>
                      <div>{pos.quantity}</div>
                      <div>{pos.startTime}</div>
                      <div>{pos.duration}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Inventory Items */}
            {selectedService.inventories && selectedService.inventories.length > 0 && (
              <div>
                <div className="text-sm font-medium mb-2">Inventory ({selectedService.inventories.length})</div>
                <div className="space-y-1 max-h-40 overflow-y-auto">
                  {selectedService.inventories.map((inv, idx) => (
                    <div key={idx} className="text-sm p-2 bg-gray-50 rounded flex justify-between">
                      <span className="font-medium">{inv.title || 'Untitled'}</span>
                      <span className="text-gray-500 text-xs">{inv.inventoryRef}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Notes */}
            {selectedService.staffNotes && (
              <div>
                <div className="text-sm font-medium mb-2">Notes</div>
                <div className="p-3 bg-gray-50 border rounded-lg text-sm">
                  {selectedService.staffNotes}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
